import { useId, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { FileMinus2 } from 'lucide-react';
import { t } from '../../../shared/i18n/index.js';
import { DESKTOP_QUERY, useMediaQuery } from '../../../shared/ui/useMediaQuery.js';
import {
  DEFAULT_WRITE_OFF_TAB,
  tabCounts,
  type WriteOffTab,
} from '../model/write-off-tabs.js';
import { useWriteOffStatusTotals, useWriteOffTabList } from '../model/write-off.queries.js';
import { WriteOffActDialog } from './WriteOffActDialog.js';
import { WriteOffCardList } from './WriteOffCard.js';
import { WriteOffListEmpty, WriteOffListError, WriteOffListSkeleton } from './WriteOffListStates.js';
import { WriteOffTabs, writeOffPanelId, writeOffTabId } from './WriteOffTabs.js';
import { WriteOffsTable } from './WriteOffsTable.js';

export interface WriteOffQueuePageProps {
  /**
   * Faol tab — berilsa sahifa BOSHQARILADI (marshrut uni manzildan oladi).
   * Berilmasa o'z holatini o'zi saqlaydi: testlar marshrutsiz chizadi.
   */
  tab?: WriteOffTab;
  onTabChange?: (tab: WriteOffTab) => void;
}

/**
 * MODDIY BUXGALTER NAVBATI.
 *
 * Sukut bo'yicha «HAMMASI» ko'rsatiladi: sidebar havolasi (`/write-offs`)
 * shu tabda ochiladi. Kutilayotgan aktlar baribir birinchi turadi — navbat
 * tartibi (server) ularni tepaga qo'yadi — va «Kutilmoqda» tabi bir
 * bosishda.
 *
 * Ro'yxat DOIRALANGAN — server o'zi filtrlaydi. Bu yerda hech qanday
 * qo'shimcha tekshiruv yo'q va bo'lmasligi ham kerak: ikki tomonda
 * ikki xil qoida bo'lsa, ular bir kun ajralib ketardi.
 *
 * Mantiq (qaysi tab, qaysi so'rov, hisoblagichlar) shu yerda; jadval va
 * kartochka faqat KO'RINISH va bir xil props oladi.
 */
export const WriteOffQueuePage = ({ tab: controlledTab, onTabChange }: WriteOffQueuePageProps) => {
  const [localTab, setLocalTab] = useState<WriteOffTab>(DEFAULT_WRITE_OFF_TAB);
  const tab = controlledTab ?? localTab;
  const setTab = (next: WriteOffTab): void => {
    if (controlledTab === undefined) setLocalTab(next);
    onTabChange?.(next);
  };

  const [openActId, setOpenActId] = useState<string | null>(null);
  const baseId = useId();
  const wide = useMediaQuery(DESKTOP_QUERY);
  const reduceMotion = useReducedMotion() === true;

  const list = useWriteOffTabList(tab);
  const counts = tabCounts(useWriteOffStatusTotals());

  const viewKey = list.isPending
    ? 'loading'
    : list.isError
      ? 'error'
      : list.items.length === 0
        ? 'empty'
        : 'list';

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <header className="flex flex-col gap-5">
        <div className="flex min-w-0 items-start gap-4">
          <span
            aria-hidden
            className="hidden size-12 shrink-0 items-center justify-center rounded-2xl bg-accent-soft text-accent ring-1 ring-accent/15 sm:flex"
          >
            <FileMinus2 size={22} />
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <h1 className="text-2xl leading-8 font-semibold tracking-tight text-balance text-text">
              {t('writeOff.title')}
            </h1>
            <p className="max-w-prose text-sm leading-5 text-pretty text-text-muted">
              {t('writeOff.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
          <WriteOffTabs baseId={baseId} value={tab} counts={counts} onChange={setTab} />
          {viewKey === 'list' ? (
            <p className="text-sm text-text-muted" aria-live="polite">
              {t('writeOff.shown', { count: list.items.length })}
            </p>
          ) : null}
        </div>
      </header>

      <section
        id={writeOffPanelId(baseId)}
        role="tabpanel"
        aria-labelledby={writeOffTabId(baseId, tab)}
        aria-busy={list.isPending}
        className="min-w-0"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`${tab}:${viewKey}`}
            initial={reduceMotion ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, y: -4 }}
            transition={{ duration: reduceMotion ? 0 : 0.16, ease: [0.4, 0, 0.2, 1] }}
          >
            {viewKey === 'loading' ? (
              <WriteOffListSkeleton wide={wide} />
            ) : viewKey === 'error' ? (
              <WriteOffListError
                message={list.error?.message ?? ''}
                onRetry={list.refetch}
              />
            ) : viewKey === 'empty' ? (
              <WriteOffListEmpty tab={tab} />
            ) : wide ? (
              <WriteOffsTable rows={list.items} onOpen={setOpenActId} reduceMotion={reduceMotion} />
            ) : (
              <WriteOffCardList rows={list.items} onOpen={setOpenActId} reduceMotion={reduceMotion} />
            )}
          </motion.div>
        </AnimatePresence>
      </section>

      {openActId === null ? null : (
        <WriteOffActDialog actId={openActId} mode="review" onClose={() => setOpenActId(null)} />
      )}
    </div>
  );
};
